import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (orderData: OrderData) => void;
  productName: string;
  price: string;
  isSubmitting: boolean;
}

interface OrderData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerMessage: string;
  productName: string;
  price: string;
}

function OrderModal({ isOpen, onClose, onSubmit, productName, price, isSubmitting }: OrderModalProps) {
  const [formData, setFormData] = useState<OrderData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerMessage: '',
    productName,
    price
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Order {productName}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="order-form">
          <div className="form-group">
            <label htmlFor="customerName">Name *</label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="customerEmail">Email *</label>
            <input
              type="email"
              id="customerEmail"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="customerPhone">Phone</label>
            <input
              type="tel"
              id="customerPhone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="customerMessage">Additional Details</label>
            <textarea
              id="customerMessage"
              name="customerMessage"
              value={formData.customerMessage}
              onChange={handleInputChange}
              placeholder="Size preferences, shipping address, or any other details..."
              rows={4}
            />
          </div>
          
          <div className="order-summary">
            <p><strong>Product:</strong> {productName}</p>
            <p><strong>Price:</strong> ${price}</p>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta-button" disabled={isSubmitting}>
              {isSubmitting ? 'Sending Order...' : 'Send Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Merch() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{ name: string; price: string } | null>(null);

  const handleOrderClick = (productName: string, price: string) => {
    setSelectedProduct({ name: productName, price });
    setModalOpen(true);
  };

  const handleOrderSubmit = async (orderData: OrderData) => {
    setIsSubmitting(true);

    try {
      const result = await emailjs.send(
        'service_fwos34t', // Replace with your EmailJS service ID
        'template_sskmgta', // Replace with your EmailJS template ID
        {
          to_email: 'wormwagonband@gmail.com',
          customer_name: orderData.customerName,
          customer_email: orderData.customerEmail,
          customer_phone: orderData.customerPhone,
          customer_message: orderData.customerMessage,
          product_name: orderData.productName,
          price: orderData.price,
          order_summary: `New order from ${orderData.customerName} (${orderData.customerEmail}) for ${orderData.productName} - $${orderData.price}`,
        },
        'j0lfsrNw2mAwTFyYf' // Replace with your EmailJS public key
      );

      if (result.status === 200) {
        alert('Order submitted successfully! We\'ll get back to you soon.');
        setModalOpen(false);
        setSelectedProduct(null);
      } else {
        alert('There was an error submitting your order. Please try again.');
      }
    } catch (error) {
      console.error('EmailJS error:', error);
      alert('There was an error submitting your order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="page-content">
      <section className="page-hero">
        <h1>Merchandise</h1>
        <p>Show your support with official band merch</p>
      </section>

      <section className="featured-merch">
        <h2>Available Items</h2>
        <div className="merch-grid">
          <div className="merch-item">
          <img src="/images/merch-photos/new shirt pic.jpg" alt="Twisted Wizard Shirt" className="merch-image" />
            <h3>Twisted Wizard Shirt</h3>
            <p className="price">$20.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('Twisted Wizard Shirt', '20.00')}
            >
              Order Now
            </button>
          </div>
          <div className="merch-item">
          <img src="/images/album-covers/dopegnome cover.jpeg" alt="Dopegnome 20x20 Poster" className="merch-image" />
            <h3>Dopegnome 20x20 Poster</h3>
            <p className="price">$25.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('Dopegnome 20x20 Poster', '25.00')}
            >
              Order Now
            </button>
          </div>
          <div className="merch-item">
          <img src="/images/merch-photos/tote pic.jpg" alt="WormWagon Tote Bag" className="merch-image" />
            <h3>WormWagon Tote Bag</h3>
            <p className="price">$15.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('WormWagon Tote Bag', '25.00')}
            >
              Order Now
            </button>
          </div>
          <div className="merch-item">
          <img src="/images/merch-photos/dope cd.jpg" alt="Dopegnome CD" className="merch-image" />
            <h3>Dopegnome CD</h3>
            <p className="price">$8.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('Dopegnome CD', '8.00')}
            >
              Order Now
            </button>
          </div>
          <div className="merch-item">
          <img src="/images/merch-photos/shirt_models.jpg" alt="WormWagon T-Shirt" className="merch-image" />
            <h3>WormWagon T-Shirt</h3>
            <p className="price">$25.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('WormWagon T-Shirt', '20.00')}
            >
              Order Now
            </button>
          </div>
          <div className="merch-item">
          <img src="/images/merch-photos/tw_cds.jpg" alt="Twisted Wizard CD" className="merch-image" />
            <h3>Twisted Wizard CD</h3>
            <p className="price">$8.00</p>
            <button 
              className="cta-button" 
              onClick={() => handleOrderClick('Twisted Wizard CD', '8.00')}
            >
              Order Now
            </button>
          </div>
        </div>
      </section>

      {selectedProduct && (
        <OrderModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedProduct(null);
          }}
          onSubmit={handleOrderSubmit}
          productName={selectedProduct.name}
          price={selectedProduct.price}
          isSubmitting={isSubmitting}
        />
      )}
    </main>
  );
}

export default Merch;